<template>
  <div>
    <div class="d-flex align-items-center mb-2">
      <LoadingIndicatorComponent
        v-if="loading && rows.length > 0"
        inline
        :message="$t('forwards.mandates.devices.loading')"
        data-testid="devices-loading"
      />
      <div class="form-check form-switch mb-0 ms-auto">
        <input
          :id="allowlistId"
          v-model="useAllowlist"
          class="form-check-input"
          type="checkbox"
          :disabled="disabled"
        />
        <label class="form-check-label small" :for="allowlistId">{{
          $t("forwards.mandates.devices.use_allowlist")
        }}</label>
      </div>
    </div>
    <!-- Remounted when the allowlist column comes or goes: PrimeVue pins dragged widths to column
         positions, which would land on the wrong columns once they shift. -->
    <DataTable
      :key="useAllowlist ? 'allowlist' : 'all-devices'"
      v-model:sortField="sortField"
      v-model:sortOrder="sortOrder"
      :value="rows"
      dataKey="device_id"
      class="border rounded"
      removableSort
      :scrollable="rows.length > 12"
      scrollHeight="420px"
      resizableColumns
      columnResizeMode="fit"
      @column-resize-end="onColumnResizeEnd"
    >
      <template #empty>
        <LoadingIndicatorComponent
          v-if="loading"
          :message="$t('forwards.mandates.devices.loading')"
          data-testid="devices-loading"
        />
        <div v-else class="text-center py-4 text-muted">
          {{ $t("forwards.mandates.devices.empty") }}
        </div>
      </template>

      <Column
        v-if="useAllowlist"
        :headerStyle="{ width: '3rem' }"
        :pt="{ columnResizer: { class: 'd-none' } }"
      >
        <template #header>
          <input
            class="form-check-input mt-0"
            type="checkbox"
            :checked="allSelectableAllowed"
            :indeterminate="someSelectableAllowed && !allSelectableAllowed"
            :disabled="disabled || selectableIds.length === 0"
            :aria-label="$t('forwards.mandates.devices.include_all')"
            @change="toggleAll"
          />
        </template>
        <template #body="{ data }">
          <input
            class="form-check-input mt-0"
            type="checkbox"
            :checked="isAllowed(data.device_id)"
            :disabled="
              disabled ||
              data.pap_locked ||
              (!!data.stale_reason && !isAllowed(data.device_id))
            "
            :aria-label="
              $t('forwards.mandates.devices.include', {
                device: data.deviceLabel,
              })
            "
            @change="toggleDevice(data.device_id)"
          />
        </template>
      </Column>

      <Column
        field="deviceLabel"
        :header="$t('forwards.mandates.devices.device')"
        :style="columnStyle('device')"
        sortable
      >
        <template #sorticon="{ sortOrder }">
          <FontAwesomeIcon
            :icon="getSortIcon(sortOrder)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <div class="fw-semibold">{{ data.deviceLabel }}</div>
          <div
            v-if="data.stale_reason"
            class="text-danger fs-10"
            data-testid="stale-device"
          >
            {{ staleText(data.stale_reason) }}
          </div>
        </template>
      </Column>

      <Column
        field="company_name"
        :header="$t('forwards.mandates.devices.company')"
        :style="columnStyle('company')"
        sortable
      >
        <template #sorticon="{ sortOrder }">
          <FontAwesomeIcon
            :icon="getSortIcon(sortOrder)"
            class="pv-datatable-sort-icon"
          />
        </template>
      </Column>

      <Column
        field="obligations"
        sortField="obligationOrder"
        :style="columnStyle('obligations')"
        sortable
      >
        <template #sorticon="{ sortOrder }">
          <FontAwesomeIcon
            :icon="getSortIcon(sortOrder)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #header>
          <InfoTooltip :text="$t('forwards.mandates.devices.obligations_note')">
            {{ $t("forwards.mandates.devices.obligations") }}
          </InfoTooltip>
        </template>
        <template #body="{ data }">
          <ForwardsStatusBadgeComponent
            v-if="data.pap_locked"
            status="pay_as_produced"
          />
          <span
            v-else-if="data.obligations"
            class="badge rounded-pill bg-warning-subtle text-warning-emphasis text-nowrap"
            data-testid="device-obligations"
            >{{ data.obligations }}</span
          >
          <span v-else class="text-body-tertiary">&mdash;</span>
        </template>
      </Column>

      <Column
        field="country"
        :header="$t('forwards.mandates.devices.country')"
        :style="columnStyle('country')"
        sortable
      >
        <template #sorticon="{ sortOrder }">
          <FontAwesomeIcon
            :icon="getSortIcon(sortOrder)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <CountryPillComponent v-if="data.country" :country="data.country" />
          <span v-else class="text-body-tertiary">{{
            $t("forwards.common.unknown")
          }}</span>
        </template>
      </Column>

      <Column
        field="technologyName"
        :header="$t('forwards.mandates.devices.technology')"
        :style="columnStyle('technology')"
        sortable
      >
        <template #sorticon="{ sortOrder }">
          <FontAwesomeIcon
            :icon="getSortIcon(sortOrder)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">{{ data.technologyName }}</template>
      </Column>

      <Column
        field="cod"
        :header="$t('forwards.mandates.devices.cod')"
        :style="columnStyle('cod')"
        sortable
      >
        <template #sorticon="{ sortOrder }">
          <FontAwesomeIcon
            :icon="getSortIcon(sortOrder)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">{{
          data.cod ? data.cod.slice(0, 7) : "—"
        }}</template>
      </Column>

      <Column
        field="support"
        sortField="supportOrder"
        :header="$t('forwards.mandates.devices.support')"
        :style="columnStyle('support')"
        sortable
      >
        <template #sorticon="{ sortOrder }">
          <FontAwesomeIcon
            :icon="getSortIcon(sortOrder)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">{{ triState(data.support) }}</template>
      </Column>

      <Column
        field="ecolabelText"
        sortField="ecolabelOrder"
        :header="$t('forwards.mandates.devices.ecolabel')"
        :style="columnStyle('ecolabel')"
        sortable
      >
        <template #sorticon="{ sortOrder }">
          <FontAwesomeIcon
            :icon="getSortIcon(sortOrder)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <span v-if="data.ecolabels.length" class="d-flex gap-1 flex-wrap">
            <span
              v-for="key in data.ecolabels"
              :key="key"
              class="badge rounded-pill ecolabel-badge"
              :class="ecolabelBadgeClass(key)"
            >
              {{ ecolabelBrandName(key) }}
            </span>
          </span>
        </template>
      </Column>

      <Column
        field="estimate"
        :header="$t('forwards.mandates.devices.estimate')"
        :style="columnStyle('estimate')"
        sortable
        dataType="numeric"
      >
        <template #sorticon="{ sortOrder }">
          <FontAwesomeIcon
            :icon="getSortIcon(sortOrder)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <span v-if="data.hasEstimate" class="text-nowrap">{{
            formatNumberWithSpaces(data.estimate)
          }}</span>
          <span v-else class="text-warning-emphasis fs-10 no-estimate-hint">
            {{ $t("forwards.mandates.volumes.no_estimate") }}
          </span>
        </template>
      </Column>
    </DataTable>
    <p class="text-body-tertiary fs-10 mt-2 mb-0">
      {{ $t("forwards.mandates.devices.estimate_note") }}
    </p>
  </div>
</template>

<script setup lang="ts">
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { ecolabelBadgeClass, ecolabelBrandName } from "@/utils/ecolabelBadges";
import { getSortIcon } from "@/utils/sortIcons";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import { usePersistedColumnWidths } from "@/composables/usePersistedColumnWidths";
import CountryPillComponent from "@/forwards2/components/CountryPillComponent.vue";
import ForwardsStatusBadgeComponent from "@/forwards2/components/ForwardsStatusBadgeComponent.vue";
import type {
  MandateDeviceRow,
  MandateDeviceScope,
  MandateDeviceStaleReason,
} from "@/forwards2/forwardsTypes";
import { isTechnologyKey, TECHNOLOGIES } from "@/types/technology";
import { compact } from "lodash-es";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { computed, ref, useId } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Every candidate device of the mandate's member companies, and — in allowlist mode — which of them
 * the mandate actually sells from.
 *
 * The list is the full candidate set rather than only the covered devices, so the allowlist can be
 * edited against everything available. A device a pay-as-produced mandate has already claimed is shown
 * but cannot be ticked: PAP device sets are exclusive.
 *
 * The Obligations column names what already claims each device — the other mandates it overlaps with
 * and the PPAs served ahead of forwards — so a device is picked knowing what else is drawing on it.
 *
 * An allowlisted device the registry sync has deleted or unregistered comes back flagged: it sells
 * nothing, and its row is there so it can be unticked. Once unticked it cannot be ticked again.
 *
 * While `loading`, the rows of a changed membership are on their way: an empty table says so instead
 * of "no devices", and rows already shown stay, marked as being replaced.
 */
const props = withDefaults(
  defineProps<{
    devices: MandateDeviceRow[];
    disabled?: boolean;
    loading?: boolean;
  }>(),
  { disabled: false, loading: false }
);

const deviceScope = defineModel<MandateDeviceScope>("deviceScope", {
  required: true,
});
const allowlistDeviceIds = defineModel<number[]>("allowlistDeviceIds", {
  required: true,
});

const { t } = useI18n();
const allowlistId = useId();
// Held here rather than in the table so a sort survives the table remounting on an allowlist toggle.
const sortField = ref<string | undefined>("deviceLabel");
const sortOrder = ref<number | undefined>(1);

const useAllowlist = computed<boolean>({
  get: () => deviceScope.value === "allowlist",
  set: (value) => {
    deviceScope.value = value ? "allowlist" : "all_devices";
    // Leaving allowlist mode drops the list: an allowlist with no rows covers nothing, so keeping
    // a stale one would silently empty the mandate if the toggle came back on.
    if (!value) allowlistDeviceIds.value = [];
    else if (allowlistDeviceIds.value.length === 0) {
      allowlistDeviceIds.value = props.devices
        .filter((device) => device.in_mandate)
        .map((device) => device.device_id);
    }
  },
});

const { columnStyle, onColumnResizeEnd } = usePersistedColumnWidths(
  "forwards2.mandate-devices.v1",
  () => [
    ...(useAllowlist.value ? [null] : []),
    "device",
    "company",
    "obligations",
    "country",
    "technology",
    "cod",
    "support",
    "ecolabel",
    "estimate",
  ]
);

/**
 * The table's rows. Every column a user can sort on carries a field whose order matches what the
 * cell shows, so sorting by Technology orders by the translated name rather than by the raw energy
 * key, the Support column reads No → Yes → Unknown (D-FIX5-1-03), and Ecolabel orders by the
 * number of qualifying labels.
 */
const rows = computed(() =>
  props.devices.map((device) => ({
    ...device,
    deviceLabel:
      device.device_name ??
      t("forwards.mandates.devices.unnamed", { id: device.device_id }),
    technologyName: technologyLabel(device),
    obligations: obligationText(device),
    obligationOrder: obligationOrder(device),
    supportOrder: triStateOrder(device.support),
    ecolabelText: device.ecolabels.map(ecolabelBrandName).join(", "),
    ecolabelOrder: device.ecolabels.length,
    hasEstimate: device.production_estimate_available,
    estimate: Object.values(device.estimated_mwh_by_period).reduce(
      (total, value) => total + value,
      0
    ),
  }))
);

const isAllowed = (deviceId: number) =>
  allowlistDeviceIds.value.includes(deviceId);

// A pay-as-produced mandate's claim cannot be overridden from here, and a stale device can only be
// unticked, so the header checkbox reads and writes only the rows the user could tick one by one.
const selectableIds = computed(() =>
  props.devices
    .filter((device) => !device.pap_locked && !device.stale_reason)
    .map((device) => device.device_id)
);
const allSelectableAllowed = computed(
  () =>
    selectableIds.value.length > 0 &&
    selectableIds.value.every((id) => isAllowed(id))
);
const someSelectableAllowed = computed(() =>
  selectableIds.value.some((id) => isAllowed(id))
);

const toggleAll = () => {
  const selectable = new Set(selectableIds.value);
  const locked = allowlistDeviceIds.value.filter((id) => !selectable.has(id));
  allowlistDeviceIds.value = allSelectableAllowed.value
    ? locked
    : [...locked, ...selectableIds.value];
};

const toggleDevice = (deviceId: number) => {
  allowlistDeviceIds.value = isAllowed(deviceId)
    ? allowlistDeviceIds.value.filter((id) => id !== deviceId)
    : [...allowlistDeviceIds.value, deviceId];
};

/**
 * What already claims the device, joined as the sketch writes it: "2 mandates", "1 mandate, 3 PPAs".
 * A zero is dropped rather than printed, and an unclaimed device gets no text at all — the cell
 * renders a dash. A pay-as-produced claim is not counted here; that row shows its badge instead.
 */
const obligationText = (device: MandateDeviceRow): string =>
  compact([
    device.mandate_count > 0
      ? t("forwards.mandates.devices.mandate_count", device.mandate_count)
      : null,
    device.ppa_count > 0
      ? t("forwards.mandates.devices.ppa_count", device.ppa_count)
      : null,
  ]).join(", ");

/**
 * Sorts by how much already claims the device, with the pay-as-produced rows past every count:
 * that claim is exclusive, which is more than any number of mandates a device can overlap with.
 */
const obligationOrder = (device: MandateDeviceRow): number =>
  device.pap_locked
    ? Number.MAX_SAFE_INTEGER
    : device.mandate_count + device.ppa_count;

const staleText = (reason: MandateDeviceStaleReason): string =>
  reason === "deleted_by_registry"
    ? t("forwards.mandates.devices.stale_deleted_by_registry")
    : t("forwards.mandates.devices.stale_not_registered");

const triState = (value: boolean | null): string => {
  if (value === null) return t("forwards.common.unknown");
  return value ? t("forwards.common.yes") : t("forwards.common.no");
};

const triStateOrder = (value: boolean | null): number => {
  if (value === null) return 2;
  return value ? 1 : 0;
};

const technologyLabel = (device: MandateDeviceRow): string => {
  const key = device.energy_type;
  if (key !== null && isTechnologyKey(key)) return TECHNOLOGIES[key].name;
  return device.technology_code ?? t("forwards.common.unknown");
};
</script>

<style lang="scss" scoped>
// The picker's take-table pill (`PickerTakeTableComponent`), so a label reads the same on both pages.
.ecolabel-badge {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  font-weight: 600;
}
</style>
